import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { useAuth } from "@/context/AuthContext";
import { adminInquiries, setInquiryStatus } from "@/services/unsaidService";
import { fmtDate } from "@/components/billing/TrialTerms";

const NOTIFY = { sent: "Email sent", failed: "Email failed", pending: "Email sending", pending_config: "Email pending setup" };

// Admin-only (server-verified role) list of professional partnership inquiries.
export default function AdminInquiries() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    const res = await adminInquiries();
    if (!res.ok) { setDenied(true); return; }
    setData(res.data);
  };
  useEffect(() => {
    if (loading || user === undefined) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    load();
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, status) => { if (await setInquiryStatus(id, status)) load(); };

  if (denied) {
    return <OnboardingLayout testId="page-admin-denied" contentClassName="items-center justify-center text-center"><p className="text-slate-600">This page is only available to FioNova administrators.</p></OnboardingLayout>;
  }
  return (
    <div className="fio-immersive min-h-screen" data-testid="page-admin-inquiries"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto max-w-4xl px-5 py-10">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Partnership inquiries</h1>
          <div className="flex gap-2 text-sm">
            <button data-testid="admin-home" onClick={() => navigate(user?.entitled ? "/app" : "/")} className="rounded-full bg-white/80 px-3 py-1.5 text-slate-700 ring-1 ring-slate-200">Home</button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/login"); }} className="rounded-full bg-white/80 px-3 py-1.5 text-slate-700 ring-1 ring-slate-200">Sign out</button>
          </div>
        </div>
        {data && !data.notifications_configured && (
          <p className="mt-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-200" data-testid="admin-notify-pending">
            Email notifications are not configured yet (set PARTNERSHIP_NOTIFY_EMAIL). Inquiries are saved here.
          </p>
        )}
        <div className="mt-6 space-y-3">
          {!data ? <p className="text-slate-500">Loading…</p> : data.inquiries.length === 0 ? <p className="text-slate-500" data-testid="admin-empty">No inquiries yet.</p> :
            data.inquiries.map((q) => (
              <div key={q.id} className="fio-glass fio-hairline rounded-3xl p-5" data-testid={`admin-inquiry-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-slate-900">{q.name} · {q.organization}</p>
                  <span className="text-xs text-slate-500">{fmtDate(q.created_at)} · {NOTIFY[q.notification_status] || q.notification_status}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{q.role} · {q.email} · {q.interest_type.replace(/_/g, " ")}</p>
                {q.message && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{q.message}</p>}
                <div className="mt-3 flex gap-2">
                  {["new", "contacted", "closed"].map((st) => (
                    <button key={st} data-testid={`admin-inquiry-${q.id}-${st}`} onClick={() => update(q.id, st)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${q.status === st ? "bg-teal-600 text-white" : "bg-white/80 text-slate-600 ring-1 ring-slate-200"}`}>{st}</button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
